import { useMemo, useState } from "react";
import { useAdminUsers } from "../hooks/useAdminUsers";
import { AdminTableSkeleton } from "../components/admin/AdminTableSkeleton";
import { useAdminEvents, type AdminAnalyticsEvent } from "../hooks/useAdminEvents";
import { ANALYTICS_EVENT, type AnalyticsEventType } from "../hooks/useAnalytics";
import { formatDateTime, formatDateTimeCompact } from "../utils/formatDateTime";
import styles from "./AdminEvents.module.css";
import { AdminPageIntro } from "../components/admin/AdminPageIntro";

// All known event types in the order we want to surface them in the
// filter chip strip. Keep the labels short — the chip row needs to wrap
// gracefully on narrow viewports.
type TypeMeta = { type: AnalyticsEventType; label: string; tone: "default" | "interest" | "view" };
const TYPE_META: ReadonlyArray<TypeMeta> = [
  { type: ANALYTICS_EVENT.integrationInterest, label: "Integration interest", tone: "interest" },
  { type: ANALYTICS_EVENT.agentInterest, label: "Agent interest", tone: "interest" },
  { type: ANALYTICS_EVENT.integrationView, label: "Integration view", tone: "view" },
  { type: ANALYTICS_EVENT.agentView, label: "Agent view", tone: "view" },
  { type: ANALYTICS_EVENT.resourceView, label: "Resource view", tone: "view" },
  { type: ANALYTICS_EVENT.openInstance, label: "Open instance", tone: "default" },
  { type: ANALYTICS_EVENT.supportClick, label: "Support click", tone: "default" },
  { type: ANALYTICS_EVENT.coachingTopupClick, label: "Top-up click", tone: "default" },
];

const META_BY_TYPE: ReadonlyMap<AnalyticsEventType, TypeMeta> = new Map(
  TYPE_META.map((m) => [m.type, m]),
);

const labelForType = (type: AnalyticsEventType): string =>
  META_BY_TYPE.get(type)?.label ?? type;

const toneForType = (type: AnalyticsEventType): string => {
  const tone = META_BY_TYPE.get(type)?.tone ?? "default";
  return tone === "interest" ? styles.typeInterest! :
    tone === "view" ? styles.typeView! :
    styles.typeDefault!;
};

export const AdminEvents = (): JSX.Element => {
  const { users } = useAdminUsers();
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedTypes, setSelectedTypes] = useState<ReadonlyArray<AnalyticsEventType>>(
    [],
  );

  const filter = useMemo(
    () => ({
      user: selectedUser || undefined,
      types: selectedTypes.length > 0 ? selectedTypes : undefined,
    }),
    [selectedUser, selectedTypes],
  );

  const { status, events, error, isFetching, refresh } = useAdminEvents(filter);

  const counts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const e of events) {
      acc[e.type] = (acc[e.type] ?? 0) + 1;
    }
    return acc;
  }, [events]);

  const toggleType = (type: AnalyticsEventType): void => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const clearFilters = (): void => {
    setSelectedUser("");
    setSelectedTypes([]);
  };

  const sortedUsers = useMemo(
    () =>
      users
        .slice()
        .sort((a, b) => a.email.localeCompare(b.email)),
    [users],
  );

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Events">
        Dashboard interactions we record: views, register-interest clicks, OpenClaw opens, support clicks and coaching top-ups. Newest first.
      </AdminPageIntro>

      <section className={styles.toolbar}>
        <label className={styles.userField}>
          <span className={styles.fieldLabel}>Customer</span>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <option value="">All customers</option>
            {sortedUsers.map((u) => (
              <option key={u.email} value={u.email}>
                {u.email}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.typeChips} role="group" aria-label="Filter by event type">
          {TYPE_META.map((meta) => {
            const active = selectedTypes.includes(meta.type);
            return (
              <button
                key={meta.type}
                type="button"
                className={`${styles.chip} ${active ? styles.chipActive : ""}`}
                onClick={() => toggleType(meta.type)}
              >
                <span>{meta.label}</span>
                <span className={styles.chipCount}>{counts[meta.type] ?? 0}</span>
              </button>
            );
          })}
        </div>

        <div className={styles.toolbarRight}>
          {(selectedUser || selectedTypes.length > 0) && (
            <button type="button" className={styles.refreshBtn} onClick={clearFilters}>
              Clear filters
            </button>
          )}
          <button type="button" className={styles.refreshBtn} onClick={() => refresh()}>
            Refresh
          </button>
        </div>
      </section>

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Customer", "Event", "Value", "Note", "When"]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && (
          <div className={styles.empty}>
            Couldn't load events{error ? ` — ${error}` : ""}.
          </div>
        )}
        {status === "ready" && events.length === 0 && (
          <div className={styles.empty}>
            No events match this filter.
          </div>
        )}
        {status === "ready" && events.length > 0 && (
          <table
            className={isFetching ? `${styles.table} ${styles.tableFetching}` : styles.table}
          >
            <thead>
              <tr>
                <th>Customer</th>
                <th>Event</th>
                <th>Value</th>
                <th>Note</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {events.map((row: AdminAnalyticsEvent) => (
                <tr key={row.id}>
                  <td className={styles.email}>
                    <span className={styles.clip} title={row.userEmail}>
                      {row.userEmail}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.typePill} ${toneForType(row.type)}`}>
                      {labelForType(row.type)}
                    </span>
                  </td>
                  <td className={styles.subject}>
                    {row.subject ? (
                      <span className={styles.clip} title={row.subject}>
                        {row.subject}
                      </span>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td className={styles.note}>
                    {row.note ? (
                      <span className={styles.noteText} title={row.note}>
                        {row.note}
                      </span>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td className={`${styles.muted} ${styles.nowrap}`} title={formatDateTime(row.createdAt)}>
                    {formatDateTimeCompact(row.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};
